import React, { useState, useEffect, useRef, useCallback } from "react";
import { HANDLE_COLORS, fetchPosts, submitPost } from "./posts";
import "./App.css";

const NAME_MAX = 10;
const MESSAGE_MAX = 100;
// Printable ASCII only — matches the backend's input policy. This is a
// client-side hint; the backend is still the source of truth.
const ASCII_PRINTABLE = /^[\x20-\x7E]*$/;

function formatTs(ts) {
  const d = new Date(ts * 1000);
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const dy = String(d.getDate()).padStart(2, "0");
  const yr = String(d.getFullYear()).slice(2);
  let hr = d.getHours();
  const ampm = hr >= 12 ? "p" : "a";
  hr = hr % 12 || 12;
  const mn = String(d.getMinutes()).padStart(2, "0");
  return `${mo}/${dy}/${yr} ${hr}:${mn}${ampm}`;
}

function Post({ id, name, message, color, ts, isNew }) {
  const [visible, setVisible] = useState(!isNew);
  const ref = useRef(null);

  useEffect(() => {
    if (!visible) {
      const t = setTimeout(() => setVisible(true), 60);
      return () => clearTimeout(t);
    }
  }, [visible]);

  useEffect(() => {
    if (isNew && ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [isNew]);

  return (
    <div ref={ref} className={`post ${visible ? "visible" : ""} ${isNew ? "new-post" : ""}`}>
      <div className="post-header">
        <span className="handle" style={{ color }}>
          {name}
        </span>
        <span className="date">{formatTs(ts)}</span>
      </div>
      <div className="post-body">{message}</div>
    </div>
  );
}

function ColorPicker({ value, onChange }) {
  return (
    <div className="color-picker" role="radiogroup" aria-label="pick a color">
      {HANDLE_COLORS.map((c) => (
        <button
          type="button"
          key={c}
          className={`swatch ${value === c ? "selected" : ""}`}
          style={{ backgroundColor: c }}
          onClick={() => onChange(c)}
          aria-label={`color ${c}`}
          title={c}
        />
      ))}
    </div>
  );
}

export default function App() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [color, setColor] = useState(
    () => HANDLE_COLORS[Math.floor(Math.random() * HANDLE_COLORS.length)]
  );
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [justPostedId, setJustPostedId] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await fetchPosts();
      setPosts(data);
      setError("");
    } catch (e) {
      setError(`couldn't load posts: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onNameChange = (e) => {
    const v = e.target.value;
    if (v.length > NAME_MAX) return;
    if (!ASCII_PRINTABLE.test(v)) return;
    setName(v);
  };

  const onMessageChange = (e) => {
    const v = e.target.value;
    if (v.length > MESSAGE_MAX) return;
    if (!ASCII_PRINTABLE.test(v)) return;
    setMessage(v);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const n = name.trim();
    const m = message.trim();
    if (!n || !m) {
      setError("name and message required");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const { id } = await submitPost({ name: n, message: m, color });
      setJustPostedId(id);
      setMessage("");
      await load();
    } catch (err) {
      setError(err.message || "submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  // Render newest -> oldest. The latest post is first (backend sorts DESC).
  const renderedPosts = posts.map((p) => (
    <Post key={p.id} {...p} isNew={p.id === justPostedId} />
  ));

  return (
    <div className="bbs">
      <header className="bbs-header">
        <h1>THE WALL</h1>
        <p className="subtitle">
          [ public message board &bull; est. 2026 ]
        </p>
      </header>

      <div className="status-bar">
        <span>
          SYS: ONLINE &bull; MSGS: {posts.length} &bull; LOAD: 0.42
        </span>
        <span className="blink">&#9608;</span>
      </div>

      <div className="wall">
        {loading ? (
          <div className="post visible" style={{ color: "#555" }}>
            loading...
          </div>
        ) : posts.length === 0 ? (
          <div className="post visible" style={{ color: "#555" }}>
            no posts yet. be the first.
          </div>
        ) : (
          renderedPosts
        )}
      </div>

      <div className="input-stack">
        <form className="input-bar" onSubmit={onSubmit}>
          <span className="prompt">&gt;</span>
          <input
            className="handle-input"
            type="text"
            placeholder="name"
            value={name}
            onChange={onNameChange}
            disabled={submitting}
          />
          <input
            className="msg-input"
            type="text"
            placeholder="say something... (ascii only)"
            value={message}
            onChange={onMessageChange}
            disabled={submitting}
            autoFocus
          />
          <button className="send-btn" type="submit" disabled={submitting}>
            {submitting ? "..." : "POST"}
          </button>
        </form>

        <div className="color-row">
          <span className="color-label">color:</span>
          <ColorPicker value={color} onChange={setColor} />
        </div>

        {error && <div className="error-bar">! {error}</div>}

        <div className="ephemeral-notice">
          name &le; {NAME_MAX} chars &bull; msg &le; {MESSAGE_MAX} chars
          &bull; ascii only &bull; one post per hour per IP
        </div>
      </div>

      <footer className="bbs-footer">
        <span>HOWDY BBS v3.0</span>
        <span>&bull;</span>
        <span>NOW WITH PERSISTENCE</span>
        <span>&bull;</span>
        <span>LAST 100 POSTS</span>
      </footer>
    </div>
  );
}
